import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import monetaryAccountService from '../services/monetaryAccountService';
import { useNotification } from '../context/NotificationContext';
import AuthContext from '../context/AuthContext';
import ConfirmActionModal from '../components/ConfirmActionModal';
import RegisterMovementModal from '../components/RegisterMovementModal';
import SortableHeader from '../components/SortableHeader';
import '../styles/general.css';

const PER_PAGE = 50;

/**
 * The monetary accounts Brintter holds.
 *
 * There is no balance column: a balance is the sum of movements, and the
 * movements module does not exist yet. It arrives with something to add up.
 */
const MonetaryAccountsListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notify } = useNotification();
  const { canManage } = useContext(AuthContext);
  // Write actions are hidden, not disabled, matching the other list pages.
  const canWrite = canManage && canManage('accounting');

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [ordering, setOrdering] = useState('name');
  const [nameFilter, setNameFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [togglingAccount, setTogglingAccount] = useState(null);
  const [toggling, setToggling] = useState(false);
  // The account whose row's Registrar movimiento was clicked; null closes the
  // modal. Recording from here saves opening the account just to add a line.
  const [movementAccount, setMovementAccount] = useState(null);

  const totalPages = Math.ceil(totalCount / PER_PAGE);

  const loadAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const params = { ordering, page };
      if (nameFilter) params.name = nameFilter;
      if (typeFilter) params.account_type = typeFilter;
      if (activeFilter) params.is_active = activeFilter;

      const response = await monetaryAccountService.listAccounts(params);
      setAccounts(response.data.results || []);
      setTotalCount(response.data.count || 0);
    } catch (err) {
      console.error('Error loading monetary accounts:', err);
      notify.error(t('accounting.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [ordering, page, nameFilter, typeFilter, activeFilter, notify, t]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const handleSortChange = (field) => {
    setOrdering((current) => (current === field ? `-${field}` : field));
    setPage(1);
  };

  const changeFilter = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  const handleToggleConfirm = async () => {
    if (!togglingAccount) return;
    setToggling(true);
    try {
      await monetaryAccountService.toggleActive(togglingAccount.id);
      notify.success(
        togglingAccount.is_active ? t('accounting.deactivated') : t('accounting.activated')
      );
      setTogglingAccount(null);
      loadAccounts();
    } catch (err) {
      console.error('Error toggling monetary account:', err);
      notify.error(t('accounting.failedToToggle'));
    } finally {
      setToggling(false);
    }
  };

  if (loading && accounts.length === 0) {
    return <div className="loading">{t('common.loading')}</div>;
  }

  return (
    <div className="page-container-wide">
      <div className="page-header">
        <h1>{t('accounting.title')}</h1>
        {canWrite && (
          <button
            onClick={() => navigate('/monetary-accounts/create')}
            className="btn btn-primary"
          >
            {t('accounting.createButton')}
          </button>
        )}
      </div>

      <div className="filter-panel">
        <div className="filter-row">
          <div className="filter-group">
            <label className="form-label" htmlFor="accountNameFilter">
              {t('accounting.filters.name')}
            </label>
            <input
              id="accountNameFilter"
              type="text"
              value={nameFilter}
              onChange={(e) => changeFilter(setNameFilter)(e.target.value)}
              className="form-input"
            />
          </div>

          <div className="filter-group">
            <label className="form-label" htmlFor="accountTypeFilter">
              {t('accounting.filters.type')}
            </label>
            <select
              id="accountTypeFilter"
              value={typeFilter}
              onChange={(e) => changeFilter(setTypeFilter)(e.target.value)}
              className="form-select"
            >
              <option value="">{t('filters.none')}</option>
              <option value="bank">{t('accounting.types.bank')}</option>
              <option value="cash">{t('accounting.types.cash')}</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="form-label" htmlFor="accountActiveFilter">
              {t('accounting.filters.active')}
            </label>
            <select
              id="accountActiveFilter"
              value={activeFilter}
              onChange={(e) => changeFilter(setActiveFilter)(e.target.value)}
              className="form-select"
            >
              <option value="">{t('filters.none')}</option>
              <option value="true">{t('common.yes')}</option>
              <option value="false">{t('common.no')}</option>
            </select>
          </div>
        </div>
      </div>

      <div className="table-wrapper">
        <table className="table">
          <thead>
            <tr>
              <SortableHeader
                field="name"
                label={t('accounting.accountName')}
                sortBy={ordering}
                onSort={handleSortChange}
              />
              <th>{t('accounting.accountType')}</th>
              <th>{t('accounting.accountNumber')}</th>
              <th>{t('accounting.clabe')}</th>
              <th className="text-center">{t('common.status')}</th>
              <th className="text-center">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {accounts.length === 0 ? (
              <tr>
                <td colSpan="6" className="empty-state">{t('accounting.noAccountsYet')}</td>
              </tr>
            ) : (
              accounts.map((row) => (
                <tr key={row.id}>
                  <td>{row.name}</td>
                  <td>{t(`accounting.types.${row.account_type}`)}</td>
                  {/* An em dash rather than an empty cell, so a cash account
                      does not read as missing data. */}
                  <td>{row.account_number || '—'}</td>
                  <td>{row.clabe || '—'}</td>
                  <td className="text-center">
                    <span className={`table-status ${row.is_active ? 'active' : 'inactive'}`}>
                      {row.is_active ? t('common.active') : t('common.inactive')}
                    </span>
                  </td>
                  <td className="text-center table-actions">
                    <button
                      onClick={() => navigate(`/monetary-accounts/${row.id}`)}
                      className="btn btn-secondary btn-small"
                    >
                      {t('accounting.view')}
                    </button>
                    {canWrite && (
                      <button
                        onClick={() => navigate(`/monetary-accounts/${row.id}/edit`)}
                        className="btn btn-primary btn-small"
                      >
                        {t('common.edit')}
                      </button>
                    )}
                    {/* Hidden on a deactivated account: it takes no new
                        movements, so the button would only lead to a 400. */}
                    {canWrite && row.is_active && (
                      <button
                        onClick={() => setMovementAccount(row)}
                        className="btn btn-accent btn-small"
                      >
                        {t('accounting.addMovement')}
                      </button>
                    )}
                    {canWrite && (
                      <button
                        onClick={() => setTogglingAccount(row)}
                        className={`btn btn-small ${row.is_active ? 'btn-danger' : 'btn-primary'}`}
                      >
                        {row.is_active ? t('accounting.deactivate') : t('accounting.activate')}
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="btn btn-primary"
          >
            {t('common.previous')}
          </button>
          <span className="pagination-info">
            {t('common.pageOf', { page, total: totalPages })}
          </span>
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="btn btn-primary"
          >
            {t('common.next')}
          </button>
        </div>
      )}

      <RegisterMovementModal
        account={movementAccount}
        isOpen={!!movementAccount}
        onClose={() => setMovementAccount(null)}
        onSaved={() => setMovementAccount(null)}
      />

      <ConfirmActionModal
        isOpen={!!togglingAccount}
        onClose={() => setTogglingAccount(null)}
        onConfirm={handleToggleConfirm}
        title={togglingAccount?.is_active
          ? t('accounting.deactivate') : t('accounting.activate')}
        message={togglingAccount?.is_active
          ? t('accounting.confirmDeactivate') : t('accounting.confirmActivate')}
        confirmLabel={t('common.confirm')}
        confirming={toggling}
      />
    </div>
  );
};

export default MonetaryAccountsListPage;
